export const EMAIL_LINK_STYLE_CSS_VARS = {
  linkColor: '--link-color',
  linkTextDecoration: '--link-text-decoration',
  linkTextDecorationStyle: '--link-text-decoration-style',
  activeLinkColor: '--active-link-color',
  activeLinkTextDecoration: '--active-link-text-decoration',
  activeLinkTextDecorationStyle: '--active-link-text-decoration-style',
} as const;

const EMAIL_LINK_CSS_VAR_NAMES = new Set<string>(Object.values(EMAIL_LINK_STYLE_CSS_VARS));
const EMAIL_LINK_CSS_VAR_PATTERN = /--(?:link|active-link)-/;

const LINK_STYLE_PROPERTIES = new Set<string>(['color', 'text-decoration', 'text-decoration-style']);
const LINK_MOUSE_EVENT_ATTRIBUTES = ['onmouseenter', 'onmouseleave'] as const;
const TEXT_DECORATION_STYLE_KEYWORDS = new Set<string>(['solid', 'double', 'dotted', 'dashed', 'wavy']);
const DEFAULT_EMAIL_LINK_COLOR = '#289fe7';

export interface InlineLinkStylingInput {
  linkColor?: string | null;
  linkUnderlineStyle?: string | null;
  mouseoverColor?: string | null;
  mouseoverUnderlineStyle?: string | null;
}

export interface InlineLinkStylingAttributes {
  style: string | null;
  onmouseenter?: string | null;
  onmouseleave?: string | null;
}

export interface LinkHoverStyleCollector {
  rules: string[];
  nextId: () => string;
}

// Email clients drop JS (onmouseenter/onmouseleave). Per-link hover/active styling is emitted
// as `#link-<n>:hover, #link-<n>:active { ... !important }` rules in the document <style> block.
export function createLinkHoverStyleCollector(idPrefix = 'link'): LinkHoverStyleCollector {
  let counter = 0;

  return {
    rules: [],
    nextId: () => `${idPrefix}-${counter++}`,
  };
}

export function parseInlineStyle(styleAttr: string | null | undefined): Record<string, string> {
  const result: Record<string, string> = {};

  if (!styleAttr?.trim()) {
    return result;
  }

  for (const declaration of styleAttr.split(';')) {
    const colonIndex = declaration.indexOf(':');

    if (colonIndex === -1) {
      continue;
    }

    const property = declaration.slice(0, colonIndex).trim();
    const value = declaration.slice(colonIndex + 1).trim();

    if (property && value) {
      result[property] = value;
    }
  }

  return result;
}

export function serializeInlineStyle(styles: Record<string, string>): string {
  return Object.entries(styles)
    .filter(([, value]) => !!value)
    .map(([property, value]) => `${property}: ${value}`)
    .join('; ');
}

function serializeInlineStyleWithImportant(styles: Record<string, string>): string {
  return Object.entries(styles)
    .filter(([, value]) => !!value)
    .map(([property, value]) => `${property}: ${value} !important`)
    .join('; ');
}

function escapeJsStyleValue(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/'/g, "\\'");
}

function underlineStyleToCss(underlineStyle: string | null | undefined): Record<string, string> {
  if (!underlineStyle) {
    return {};
  }

  if (underlineStyle === 'none') {
    return { 'text-decoration': 'none' };
  }

  return { 'text-decoration': `underline ${underlineStyle}` };
}

function underlineStyleToJsSetter(underlineStyle: string | null | undefined): string | null {
  if (!underlineStyle) {
    return null;
  }

  if (underlineStyle === 'none') {
    return "this.style.textDecoration='none'";
  }

  return `this.style.textDecorationStyle='${escapeJsStyleValue(underlineStyle)}'`;
}

function extractDecorationStyleFromShorthand(value: string | null | undefined): string | null {
  if (!value) {
    return null;
  }

  const token = value
    .toLowerCase()
    .split(/\s+/)
    .find(part => TEXT_DECORATION_STYLE_KEYWORDS.has(part));

  return token ?? null;
}

function parseJsStyleAssignment(handler: string | null | undefined, property: string): string | null {
  if (!handler) {
    return null;
  }

  const pattern = new RegExp(`this\\.style\\.${property}\\s*=\\s*'([^']*)'`, 'i');
  const match = handler.match(pattern);

  return match?.[1] ?? null;
}

export function buildInlineLinkStylingAttributes(input: InlineLinkStylingInput): InlineLinkStylingAttributes {
  const styleParts: Record<string, string> = {};
  const mouseenterParts: string[] = [];
  const mouseleaveParts: string[] = [];

  if (input.linkColor) {
    styleParts.color = input.linkColor;
    mouseleaveParts.push(`this.style.color='${escapeJsStyleValue(input.linkColor)}'`);
  }

  Object.assign(styleParts, underlineStyleToCss(input.linkUnderlineStyle));

  const linkUnderlineLeave = underlineStyleToJsSetter(input.linkUnderlineStyle);

  if (linkUnderlineLeave) {
    mouseleaveParts.push(linkUnderlineLeave);
  }

  if (input.mouseoverColor) {
    mouseenterParts.push(`this.style.color='${escapeJsStyleValue(input.mouseoverColor)}'`);
  }

  const mouseoverUnderlineEnter = underlineStyleToJsSetter(input.mouseoverUnderlineStyle);

  if (mouseoverUnderlineEnter) {
    mouseenterParts.push(mouseoverUnderlineEnter);
  }

  const style = serializeInlineStyle(styleParts);

  return {
    style: style || null,
    onmouseenter: mouseenterParts.length ? mouseenterParts.join(';') : null,
    onmouseleave:
      input.mouseoverColor || input.mouseoverUnderlineStyle
        ? mouseleaveParts.length
          ? mouseleaveParts.join(';')
          : null
        : null,
  };
}

// Builds the :hover/:active css declarations from mouseover settings and global active-link theme.
function buildLinkHoverDeclarations(
  input: InlineLinkStylingInput,
  cssVariables: Record<string, string | number>,
): Record<string, string> {
  const declarations: Record<string, string> = {};

  const fontSize = resolveGlobalLinkCssValue(cssVariables, '--active-link-font-size');
  if (fontSize) {
    declarations['font-size'] = fontSize;
  }

  const fontFamily = resolveGlobalLinkCssValue(cssVariables, '--active-link-font-family');
  if (fontFamily) {
    declarations['font-family'] = fontFamily;
  }

  const backgroundColor = resolveGlobalLinkCssValue(cssVariables, '--active-link-background-color');
  if (backgroundColor) {
    declarations['background-color'] = backgroundColor;
  }

  const color = input.mouseoverColor ?? resolveGlobalLinkCssValue(cssVariables, '--active-link-color');
  if (color) {
    declarations.color = color;
  }

  if (input.mouseoverUnderlineStyle === 'none') {
    declarations['text-decoration'] = 'none';
    return declarations;
  }

  if (input.mouseoverUnderlineStyle) {
    declarations['text-decoration'] = 'underline';
    declarations['text-decoration-style'] = input.mouseoverUnderlineStyle;
    return declarations;
  }

  const activeDecoration = resolveGlobalLinkCssValue(cssVariables, '--active-link-text-decoration');
  const activeDecorationStyle = resolveGlobalLinkCssValue(
    cssVariables,
    '--active-link-text-decoration-style',
  );

  if (activeDecoration === 'none') {
    declarations['text-decoration'] = 'none';
    return declarations;
  }

  if (activeDecorationStyle) {
    declarations['text-decoration'] = 'underline';
    declarations['text-decoration-style'] = activeDecorationStyle;
    return declarations;
  }

  if (activeDecoration && /\bunderline\b/i.test(activeDecoration)) {
    declarations['text-decoration'] = 'underline';
    declarations['text-decoration-style'] = 'solid';
  }

  return declarations;
}

function buildPerLinkHoverActiveRule(linkId: string, declarations: Record<string, string>): string {
  const serialized = serializeInlineStyleWithImportant(declarations);

  if (!serialized) {
    return '';
  }

  return `#${linkId}:hover, #${linkId}:active { ${serialized} }`;
}

export function parseInlineLinkStylingFromAttributes(
  styleAttr: string | null | undefined,
  onmouseenter: string | null | undefined,
  onmouseleave: string | null | undefined,
): {
  linkColor: string | null;
  linkUnderlineStyle: string | null;
  mouseoverColor: string | null;
  mouseoverUnderlineStyle: string | null;
} {
  const styles = parseInlineStyle(styleAttr);
  const legacyLinkColor = styles[EMAIL_LINK_STYLE_CSS_VARS.linkColor];
  const legacyLinkDecoration = styles[EMAIL_LINK_STYLE_CSS_VARS.linkTextDecoration];
  const legacyLinkDecorationStyle = styles[EMAIL_LINK_STYLE_CSS_VARS.linkTextDecorationStyle];
  const legacyActiveColor = styles[EMAIL_LINK_STYLE_CSS_VARS.activeLinkColor];
  const legacyActiveDecoration = styles[EMAIL_LINK_STYLE_CSS_VARS.activeLinkTextDecoration];
  const legacyActiveDecorationStyle = styles[EMAIL_LINK_STYLE_CSS_VARS.activeLinkTextDecorationStyle];

  let linkUnderlineStyle: string | null = null;

  if (styles['text-decoration'] === 'none' || legacyLinkDecoration === 'none') {
    linkUnderlineStyle = 'none';
  }

  if (!linkUnderlineStyle) {
    linkUnderlineStyle =
      styles['text-decoration-style'] ??
      extractDecorationStyleFromShorthand(styles['text-decoration']) ??
      legacyLinkDecorationStyle ??
      null;
  }

  const mouseoverColor =
    parseJsStyleAssignment(onmouseenter, 'color') ??
    (legacyActiveColor ? legacyActiveColor : null);
  const mouseoverUnderlineStyle =
    parseJsStyleAssignment(onmouseenter, 'textDecorationStyle') ??
    (legacyActiveDecoration === 'none'
      ? 'none'
      : legacyActiveDecorationStyle
        ? legacyActiveDecorationStyle
        : null);

  return {
    linkColor: styles.color ?? legacyLinkColor ?? null,
    linkUnderlineStyle,
    mouseoverColor,
    mouseoverUnderlineStyle,
  };
}

function resolveCssVarReference(
  value: string,
  cssVariables: Record<string, string | number>,
): string {
  const trimmed = value.trim();

  if (!trimmed.toLowerCase().startsWith('var(')) {
    return trimmed;
  }

  const match = trimmed.match(/^var\(\s*(--[A-Za-z0-9_-]+)\s*(?:,\s*([^)]+))?\)$/i);

  if (!match) {
    return trimmed;
  }

  const variableName = match[1];
  const fallback = match[2]?.trim();
  const resolved = cssVariables[variableName];

  if (resolved === undefined || resolved === null || resolved === '') {
    return fallback ?? trimmed;
  }

  return resolveCssVarReference(String(resolved), cssVariables);
}

function resolveLegacyCssVarStyles(
  styles: Record<string, string>,
  cssVariables: Record<string, string | number>,
): InlineLinkStylingInput {
  const linkColor = styles[EMAIL_LINK_STYLE_CSS_VARS.linkColor];
  const activeLinkColor = styles[EMAIL_LINK_STYLE_CSS_VARS.activeLinkColor];
  const linkDecoration = styles[EMAIL_LINK_STYLE_CSS_VARS.linkTextDecoration];
  const linkDecorationStyle = styles[EMAIL_LINK_STYLE_CSS_VARS.linkTextDecorationStyle];
  const activeDecoration = styles[EMAIL_LINK_STYLE_CSS_VARS.activeLinkTextDecoration];
  const activeDecorationStyle = styles[EMAIL_LINK_STYLE_CSS_VARS.activeLinkTextDecorationStyle];

  let linkUnderlineStyle: string | null = null;

  if (linkDecoration === 'none') {
    linkUnderlineStyle = 'none';
  }

  if (!linkUnderlineStyle && linkDecorationStyle) {
    linkUnderlineStyle = linkDecorationStyle;
  }

  let mouseoverUnderlineStyle: string | null = null;

  if (activeDecoration === 'none') {
    mouseoverUnderlineStyle = 'none';
  }

  if (!mouseoverUnderlineStyle && activeDecorationStyle) {
    mouseoverUnderlineStyle = activeDecorationStyle;
  }

  return {
    linkColor: linkColor ? resolveCssVarReference(linkColor, cssVariables) : null,
    linkUnderlineStyle,
    mouseoverColor: activeLinkColor ? resolveCssVarReference(activeLinkColor, cssVariables) : null,
    mouseoverUnderlineStyle,
  };
}

function resolveGlobalLinkCssValue(
  cssVariables: Record<string, string | number>,
  name: string,
): string | null {
  const raw = cssVariables[name];

  if (raw === undefined || raw === null || String(raw).trim() === '') {
    return null;
  }

  const resolved = resolveCssVarReference(String(raw), cssVariables).trim();

  return resolved ? resolved : null;
}

function resolveGlobalUnderlineStyle(decoration: string | null, decorationStyle: string | null): string | null {
  if (decoration === 'none') {
    return 'none';
  }

  if (decorationStyle) {
    return decorationStyle;
  }

  if (decoration && /\bunderline\b/i.test(decoration)) {
    return 'solid';
  }

  return null;
}

// Builds the link styling input from the global theme css variables so that links
// using "From Global" settings still receive fully inlined style attributes and
// matching per-link #link-<n>:hover/:active rules in the exported HTML/HBS.
export function resolveGlobalLinkStylingInput(
  cssVariables: Record<string, string | number>,
  fallbackColor?: string | null,
): InlineLinkStylingInput {
  const linkColor = resolveGlobalLinkCssValue(cssVariables, EMAIL_LINK_STYLE_CSS_VARS.linkColor);
  const linkDecoration = resolveGlobalLinkCssValue(cssVariables, EMAIL_LINK_STYLE_CSS_VARS.linkTextDecoration);
  const linkDecorationStyle = resolveGlobalLinkCssValue(
    cssVariables,
    EMAIL_LINK_STYLE_CSS_VARS.linkTextDecorationStyle,
  );
  const activeColor = resolveGlobalLinkCssValue(cssVariables, EMAIL_LINK_STYLE_CSS_VARS.activeLinkColor);
  const activeDecoration = resolveGlobalLinkCssValue(cssVariables, EMAIL_LINK_STYLE_CSS_VARS.activeLinkTextDecoration);
  const activeDecorationStyle = resolveGlobalLinkCssValue(
    cssVariables,
    EMAIL_LINK_STYLE_CSS_VARS.activeLinkTextDecorationStyle,
  );

  return {
    linkColor: linkColor ?? fallbackColor ?? DEFAULT_EMAIL_LINK_COLOR,
    linkUnderlineStyle: resolveGlobalUnderlineStyle(linkDecoration, linkDecorationStyle),
    mouseoverColor: activeColor,
    mouseoverUnderlineStyle: resolveGlobalUnderlineStyle(activeDecoration, activeDecorationStyle),
  };
}

function removeManagedLinkAttributes(attrs: string): string {
  let nextAttrs = attrs;

  nextAttrs = nextAttrs.replace(/\s*style\s*=\s*(["'])(.*?)\1/i, (match, quote, styleAttr) => {
    const styles = parseInlineStyle(styleAttr);
    const cleaned = Object.fromEntries(
      Object.entries(styles).filter(
        ([property]) => !EMAIL_LINK_CSS_VAR_NAMES.has(property) && !LINK_STYLE_PROPERTIES.has(property),
      ),
    );
    const serialized = serializeInlineStyle(cleaned);

    if (!serialized) {
      return '';
    }

    return ` style=${quote}${serialized}${quote}`;
  });

  LINK_MOUSE_EVENT_ATTRIBUTES.forEach(attributeName => {
    nextAttrs = nextAttrs.replace(new RegExp(`\\s*${attributeName}\\s*=\\s*(["'])(.*?)\\1`, 'i'), '');
  });

  return nextAttrs;
}

function getAttributeValue(attrs: string, attributeName: string): string | null {
  const match = attrs.match(new RegExp(`${attributeName}\\s*=\\s*(["'])(.*?)\\1`, 'i'));
  return match?.[2] ?? null;
}

function setAttributeValue(attrs: string, attributeName: string, value: string | null | undefined): string {
  const withoutAttribute = attrs.replace(
    new RegExp(`\\s*${attributeName}\\s*=\\s*(["'])(.*?)\\1`, 'i'),
    '',
  );

  if (!value) {
    return withoutAttribute;
  }

  return `${withoutAttribute} ${attributeName}="${value}"`;
}

export function applyPerLinkEmailStyles(
  html: string,
  cssVariables: Record<string, string | number>,
  fallbackColor?: string | null,
  hoverCollector?: LinkHoverStyleCollector,
): string {
  if (!html) {
    return html;
  }

  const globalInput = resolveGlobalLinkStylingInput(cssVariables, fallbackColor);

  return html.replace(/<a(\s[^>]*?)>/gi, (match, attrs) => {
    const styleMatch = attrs.match(/style\s*=\s*(["'])(.*?)\1/i);
    const styleAttr = styleMatch?.[2] ?? '';
    const styles = parseInlineStyle(styleAttr);
    const onmouseenter = attrs.match(/onmouseenter\s*=\s*(["'])(.*?)\1/i)?.[2] ?? null;
    const onmouseleave = attrs.match(/onmouseleave\s*=\s*(["'])(.*?)\1/i)?.[2] ?? null;
    const hasLegacyCssVars = EMAIL_LINK_CSS_VAR_PATTERN.test(styleAttr);

    const parsed = parseInlineLinkStylingFromAttributes(styleAttr, onmouseenter, onmouseleave);
    const legacyResolved = hasLegacyCssVars ? resolveLegacyCssVarStyles(styles, cssVariables) : {};
    const inlineInput: InlineLinkStylingInput = {
      linkColor: parsed.linkColor ?? legacyResolved.linkColor ?? globalInput.linkColor ?? null,
      linkUnderlineStyle:
        parsed.linkUnderlineStyle ?? legacyResolved.linkUnderlineStyle ?? globalInput.linkUnderlineStyle ?? null,
      mouseoverColor: parsed.mouseoverColor ?? legacyResolved.mouseoverColor ?? globalInput.mouseoverColor ?? null,
      mouseoverUnderlineStyle:
        parsed.mouseoverUnderlineStyle ??
        legacyResolved.mouseoverUnderlineStyle ??
        globalInput.mouseoverUnderlineStyle ??
        null,
    };

    if (
      !inlineInput.linkColor &&
      !inlineInput.linkUnderlineStyle &&
      !inlineInput.mouseoverColor &&
      !inlineInput.mouseoverUnderlineStyle
    ) {
      return match;
    }

    const built = buildInlineLinkStylingAttributes(inlineInput);
    const baseStyles = parseInlineStyle(styleAttr);
    const cleanedStyles = Object.fromEntries(
      Object.entries(baseStyles).filter(
        ([property]) => !EMAIL_LINK_CSS_VAR_NAMES.has(property) && !LINK_STYLE_PROPERTIES.has(property),
      ),
    );
    const mergedStyle = serializeInlineStyle({
      ...cleanedStyles,
      ...parseInlineStyle(built.style ?? ''),
    });

    let updatedAttrs = removeManagedLinkAttributes(attrs);
    updatedAttrs = setAttributeValue(updatedAttrs, 'style', mergedStyle || null);

    const hoverDeclarations = buildLinkHoverDeclarations(inlineInput, cssVariables);

    if (hoverCollector && Object.keys(hoverDeclarations).length) {
      let linkId = getAttributeValue(updatedAttrs, 'id');

      if (!linkId) {
        linkId = hoverCollector.nextId();
        updatedAttrs = setAttributeValue(updatedAttrs, 'id', linkId);
      }

      const hoverActiveRule = buildPerLinkHoverActiveRule(linkId, hoverDeclarations);

      if (hoverActiveRule) {
        hoverCollector.rules.push(hoverActiveRule);
      }
    }

    return `<a${updatedAttrs}>`;
  });
}
